import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftRight, TrendingUp, TrendingDown, SlidersHorizontal,
  PackageCheck, Search, RefreshCw,
} from 'lucide-react';
import Layout from '../../components/Layout';
import { StockMovement } from '../../types';
import { stockService } from '../../services/stockService';
import './StockMovementPage.css';

const MOVEMENT_TYPES = [
  'GoodsReceipt',
  'ADJUSTMENT',
  'TransferOut',
  'TransferIn',
  'DamageAdjustment',
] as const;

const movementBadgeClass = (type: string): string => {
  switch (type) {
    case 'GoodsReceipt':    return 'sm-badge sm-badge-grn';
    case 'TransferIn':      return 'sm-badge sm-badge-in';
    case 'TransferOut':     return 'sm-badge sm-badge-out';
    case 'ADJUSTMENT':      return 'sm-badge sm-badge-adj';
    case 'DamageAdjustment': return 'sm-badge sm-badge-adj';
    default:                return 'sm-badge sm-badge-default';
  }
};

const movementLabel = (type: string): string => {
  switch (type) {
    case 'GoodsReceipt':    return 'Goods Receipt';
    case 'TransferIn':      return 'Transfer In';
    case 'TransferOut':     return 'Transfer Out';
    case 'ADJUSTMENT':      return 'Adjustment';
    case 'DamageAdjustment': return 'Damage Adjustment';
    default:                return type;
  }
};

const StockMovementPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [movements,   setMovements]   = useState<StockMovement[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [typeFilter,  setTypeFilter]  = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems,  setTotalItems]  = useState(0);
  const pageSize = 30;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await stockService.getStockMovements({ pageNumber: currentPage, pageSize });
      const payload = res.data as any;
      const data: StockMovement[] = payload?.data ?? payload ?? [];
      setMovements(data);
      setTotalItems(payload?.pagination?.total ?? data.length);
    } catch {
      setError(t('stock.failedToLoad', 'Failed to load stock movements'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, t]);

  useEffect(() => { load(); }, [load]);

  const filtered = movements.filter(m => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (m.productName?.toLowerCase().includes(term) || false) ||
      (m.productSKU?.toLowerCase().includes(term)  || false) ||
      (m.warehouseName?.toLowerCase().includes(term) || false) ||
      (m.notes?.toLowerCase().includes(term) || false);
    const matchesType = !typeFilter || m.movementType === typeFilter;
    return matchesSearch && matchesType;
  });

  // Stats from current page
  const totalIn  = movements.filter(m => m.quantity > 0).reduce((s, m) => s + m.quantity, 0);
  const totalOut = movements.filter(m => m.quantity < 0).reduce((s, m) => s + Math.abs(m.quantity), 0);
  const grnCount = movements.filter(m => m.movementType === 'GoodsReceipt').length;
  const adjCount = movements.filter(m => m.movementType === 'ADJUSTMENT').length;

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmt = (d: string) => new Intl.DateTimeFormat(langCode, {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(new Date(d));

  return (
    <Layout>
      <div className="stock-movement-page">

        {/* Header */}
        <div className="ims-list-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--ims-accent-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--ims-accent)',
            }}>
              <ArrowLeftRight size={20} />
            </div>
            <div>
              <h1 className="ims-list-title">{t('stock.movements', 'Stock Movements')}</h1>
              <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, margin: 0 }}>
                {t('stock.movementsDesc', 'Full audit trail of all stock in/out and adjustments')}
              </p>
            </div>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={() => load(true)} disabled={refreshing || loading}>
              <RefreshCw size={14} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh', 'Refresh')}
            </button>
            <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/stock/levels')}>
              ← {t('stock.backToLevels', 'Stock Levels')}
            </button>
          </div>
        </div>

        {/* Stat chips */}
        {!loading && (
          <div className="ims-stats-row">
            <div className="ims-stat-card">
              <div className="ims-stat-icon sm-icon-total"><ArrowLeftRight size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{totalItems}</span>
                <span className="ims-stat-label">{t('stock.totalMovements', 'Total Movements')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sm-icon-in"><TrendingUp size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">+{totalIn}</span>
                <span className="ims-stat-label">{t('stock.stockIn', 'Stock In')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sm-icon-out"><TrendingDown size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">-{totalOut}</span>
                <span className="ims-stat-label">{t('stock.stockOut', 'Stock Out')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sm-icon-grn"><PackageCheck size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{grnCount}</span>
                <span className="ims-stat-label">{t('stock.goodsReceipts', 'Goods Receipts')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon sm-icon-adj"><SlidersHorizontal size={20} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{adjCount}</span>
                <span className="ims-stat-label">{t('stock.adjustments', 'Adjustments')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && <div className="ims-alert ims-alert-error">{error}</div>}

        {/* Filters */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('common.filter', 'Filter')}
            </span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="sm-filter-bar">
              <div className="sm-filter-group" style={{ flex: 2 }}>
                <span className="sm-filter-label">{t('common.search', 'Search')}</span>
                <input
                  type="text"
                  className="ims-form-control"
                  placeholder={t('stock.movementsSearchPlaceholder', 'Product name, SKU, warehouse, notes…')}
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <div className="sm-filter-group">
                <span className="sm-filter-label">{t('stock.movementType', 'Movement Type')}</span>
                <select
                  className="ims-form-control"
                  value={typeFilter}
                  onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">{t('common.all', 'All Types')}</option>
                  {MOVEMENT_TYPES.map(type => (
                    <option key={type} value={type}>{movementLabel(type)}</option>
                  ))}
                </select>
              </div>
              {(searchTerm || typeFilter) && (
                <button
                  className="ims-btn ims-btn-secondary"
                  style={{ alignSelf: 'flex-end' }}
                  onClick={() => { setSearchTerm(''); setTypeFilter(''); setCurrentPage(1); }}
                >
                  {t('common.clearFilters', 'Clear')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="ims-card" style={{ marginTop: 0 }}>
          <div className="ims-card-header">
            <span className="ims-card-title">
              <ArrowLeftRight size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('stock.movements', 'Stock Movements')}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ims-text-sub)' }}>
              {t('stock.showingOf', 'Showing {{count}} of {{total}}', {
                count: filtered.length,
                total: totalItems,
              })}
            </span>
          </div>

          {loading ? (
            <div className="ims-card-body" style={{ padding: '48px 0', textAlign: 'center' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">{t('common.loading', 'Loading…')}</span>
              </div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>{t('common.dateTime', 'Date / Time')}</th>
                      <th>{t('common.product', 'Product')}</th>
                      <th>{t('stock.warehouse', 'Warehouse')}</th>
                      <th>{t('stock.movementType', 'Type')}</th>
                      <th className="num">{t('stock.quantity', 'Qty')}</th>
                      <th>{t('stock.relatedDocument', 'Document')}</th>
                      <th>{t('common.notes', 'Notes')}</th>
                      <th>{t('common.createdBy', 'By')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ims-text-muted)' }}>
                          {t('stock.noMovements', 'No stock movements found')}
                        </td>
                      </tr>
                    ) : (
                      filtered.map(m => (
                        <tr key={m.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ims-text-main)' }}>
                              {fmt(m.createdAt).split(',')[0]}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--ims-text-muted)' }}>
                              {new Date(m.createdAt).toLocaleTimeString(langCode, { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ims-text-main)' }}>
                              {m.productName ?? `Product #${m.productId}`}
                            </div>
                            {m.productSKU && (
                              <div style={{ fontSize: 11, color: 'var(--ims-text-muted)', fontFamily: 'monospace' }}>
                                {m.productSKU}
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--ims-text-sub)' }}>
                            {m.warehouseName ?? (m.warehouseId ? `#${m.warehouseId}` : '—')}
                          </td>
                          <td>
                            <span className={movementBadgeClass(m.movementType)}>
                              {movementLabel(m.movementType)}
                            </span>
                          </td>
                          <td className="num">
                            <span className={m.quantity >= 0 ? 'sm-qty-positive' : 'sm-qty-negative'}>
                              {m.quantity > 0 ? '+' : ''}{m.quantity}
                            </span>
                          </td>
                          <td>
                            {m.relatedDocumentType ? (
                              <div>
                                <div style={{ fontSize: 11, color: 'var(--ims-text-sub)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                  {m.relatedDocumentType}
                                </div>
                                {m.relatedDocumentId && (
                                  <span className="sm-doc-pill">#{m.relatedDocumentId}</span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--ims-text-sub)', maxWidth: 180 }}>
                            {m.notes || '—'}
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--ims-text-sub)' }}>
                            {m.createdByName ?? (m.createdBy ? `#${m.createdBy}` : '—')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="sm-pagination">
                <span>
                  {t('stock.showingOf', 'Showing {{count}} of {{total}}', {
                    count: filtered.length,
                    total: totalItems,
                  })}
                </span>
                <div className="sm-page-btns">
                  <button
                    className="sm-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ← {t('common.previous', 'Prev')}
                  </button>
                  <span className="sm-page-info">
                    {t('common.pageOf', 'Page {{page}} of {{total}}', { page: currentPage, total: totalPages })}
                  </span>
                  <button
                    className="sm-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    {t('common.next', 'Next')} →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

      </div>
    </Layout>
  );
};

export default StockMovementPage;
